import { defineDocument } from '../../schema';
export const gameOkieDokieDocument = defineDocument({
    id: 'game:okie-dokie',
    kind: 'game',
    slug: 'okie-dokie',
    route: '/discover/games/okie-dokie',
    seo: {
        title: 'Discover Okie Dokie',
        description: "Players file theme ideas, answer fresh case prompts from Poppy, stamp favorites, and chase the title of Airhead County's Okayest Bureaucrat in five turns.",
        imageAsset: 'coverOkieDokie',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Discover Game',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Okie Dokie',
            },
        ],
        description: [
            {
                type: 'text',
                text: "Players submit case themes, answer Poppy's official paperwork prompts, and stamp favorite filings into the county record.",
            },
        ],
        imageAsset: 'coverOkieDokie',
        imageAlt: 'Okie Dokie game cover',
        showDescription: true,
        background: 'blurred-image',
    },
    aside: {
        showToc: true,
    },
    sections: [
        {
            id: 'game-description',
            title: [
                {
                    type: 'text',
                    text: 'Game Description',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "Welcome to the Bureau of Okie Dokie, Airhead County's department for approving absolutely everything.",
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "Players first build the day's docket of cases, then help complete the paperwork before stamping their favorite answers to be officially filed.",
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "At the end of the day, the bureau closes with a memo recommending what Airhead County should do about a single filing from the day's caseload. In true government fashion, everything else will be pending indefinitely.",
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "Most points wins the honor every civil servant dreams of: Airhead County's Okayest Bureaucrat.",
                        },
                    ],
                },
            ],
        },
        {
            id: 'facts',
            title: [
                {
                    type: 'text',
                    text: 'Facts',
                },
            ],
            blocks: [
                {
                    type: 'facts',
                    items: [
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Players',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '3-8 players',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Game Type',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Free-For-All',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Mechanics',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Free Input, Selection, Voting',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Average Duration',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '~18 minutes',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Release Date',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'June 20, 2026',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Cost',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '1 Loon',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'clips',
            title: [
                {
                    type: 'text',
                    text: 'Clips',
                },
            ],
            widgets: [
                {
                    type: 'clipScroller',
                    gameId: '6a3349523db03ba5ecbf1db2',
                },
            ],
        },
        {
            id: 'play-sequence',
            title: [
                {
                    type: 'text',
                    text: 'Play Sequence',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Okie Dokie is played over five turns: the docket, three cases, and one final End of Day Memo.',
                        },
                    ],
                },
                {
                    type: 'list',
                    ordered: true,
                    items: [
                        [
                            {
                                type: 'text',
                                text: "The docket opens the game. Poppy starts players with a set of case-theme candidates, and each player has the opportunity to file one additional theme idea for the day's agenda.",
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Players vote for their favorite three themes from the docket. The consensus-selected themes become Case 1, Case 2, and Case 3, with the most popular selected theme saved for the final case.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Each case turns the selected theme into several prompts that appear as official paperwork for the Bureau. Players are assigned two prompts and write answers to fill in the blanks.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: `After the answering window closes, players vote for their favorite responses to each prompt. Every vote is an "OK" stamp worth points to the author of the answer.`,
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: "After all three cases, the End of Day Memo uses one notable filing from the day's caseload. This is an all-play prompt where everyone writes a recommendation for how Airhead County should handle the situation.",
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Again, players vote for their favorite answer and points are doled out to the authors. The final scores are tallied to determine the winner.',
                            },
                        ],
                    ],
                },
            ],
        },
        {
            id: 'scoring-winning',
            title: [
                {
                    type: 'text',
                    text: 'Scoring & Winning',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Earning points:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Docket: no points awarded for receiving votes on your docket submissions.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Case 1: each stamp received is worth 100 points.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Case 2: each stamp received is worth 150 points.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Case 3: each stamp received is worth 200 points.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'End of Day Memo: each stamp received is worth 150 points.',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "Players cannot vote when their answer is going head-to-head against another player's answer during the case rounds. Players can vote for their own answers during the docket vote, but not the End of Day Memo.",
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "The player with the highest total score after the memo vote is certified as Airhead County's Okayest Bureaucrat. Tied players share the title.",
                        },
                    ],
                },
            ],
        },
        {
            id: 'generated-content',
            title: [
                {
                    type: 'text',
                    text: 'Generated Content',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Players:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Docket case-theme submissions',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Answers for assigned case-file prompts',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'End of Day Memo recommendations',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Poppy AI:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Docket case-theme candidates',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Official document prompts based on selected themes',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Fallback answers when players leave assigned paperwork blank',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'End of Day Memo prompt',
                            },
                        ],
                    ],
                },
            ],
        },
        {
            id: 'other-notes',
            title: [
                {
                    type: 'text',
                    text: 'Other Notes',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Brain power required: medium',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Popular with: creatives, comedy fans, casual gamers, the quick-witted',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Read more: ',
                        },
                        {
                            type: 'link',
                            href: '/blog/okie-dokie-stamps-its-way-into-the-game-library',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Okie Dokie blog post',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'songs',
            title: [
                {
                    type: 'text',
                    text: 'Songs',
                },
            ],
            widgets: [
                {
                    type: 'songTable',
                    gameId: '6a3349523db03ba5ecbf1db2',
                },
            ],
        },
    ],
});
//# sourceMappingURL=okie-dokie.js.map